// ── Daily World Quest progress (local-first) ─────────────────────────
// Persist per-date quest progress so kids can resume mid-run and pick
// up right where they left off. Missed days don't expire — we just
// keep the record indefinitely, small enough to be a non-issue.
import { addStars, markCountryLearned } from "@/lib/progress";
import { trackEvent } from "@/lib/analytics";
import { stampCountry } from "@/lib/progression";

const KEY = "we_daily_quest_progress_v1";
// Per-completion star rewards.
const STAR_PER_STOP = 1;
const STAR_ON_FINISH = 3;

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) || {};
  } catch { /* ignore */ }
  return {};
}

function save(state) {
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
}

/** Progress record for the given quest date. Guaranteed shape:
 *  `{ completedStops: [id...], startedAt, finishedAt, celebrated }`. */
export function questProgress(dateIso) {
  const all = load();
  return all[dateIso] || { completedStops: [], startedAt: null, finishedAt: null, celebrated: false };
}

/** Mark a stop as done. Idempotent — replaying doesn't double-reward.
 *  Fires the analytics events named in the Emergent command. Returns
 *  the fresh progress object. */
export function completeStop(quest, stopId) {
  const all = load();
  const rec = all[quest.date] || { completedStops: [], startedAt: null, finishedAt: null, celebrated: false };
  if (!rec.startedAt) {
    rec.startedAt = new Date().toISOString();
    try { trackEvent("daily_quest_started", { date: quest.date, country: quest.country.code }); } catch { /* ignore */ }
  }
  const set = new Set(rec.completedStops);
  const wasDone = set.has(stopId);
  set.add(stopId);
  rec.completedStops = [...set];
  const finished = rec.completedStops.length >= quest.stops.length;
  if (finished && !rec.finishedAt) {
    rec.finishedAt = new Date().toISOString();
  }
  all[quest.date] = rec;
  save(all);

  if (!wasDone) {
    try {
      trackEvent("daily_quest_stop_completed", {
        date: quest.date,
        country: quest.country.code,
        stop_id: stopId,
        stops_done: rec.completedStops.length,
        total_stops: quest.stops.length,
      });
    } catch { /* ignore */ }
    // Partial completion still rewards — one star per stop.
    addStars(STAR_PER_STOP, `Daily quest · ${quest.country.name} · ${stopId}`);
  }

  if (finished && !rec.celebrated) {
    rec.celebrated = true;
    all[quest.date] = rec;
    save(all);
    try { trackEvent("daily_quest_completed", { date: quest.date, country: quest.country.code, stops: quest.stops.length }); } catch { /* ignore */ }
    // Finale reward: passport country stamp + bonus stars.
    try {
      markCountryLearned(quest.country.code);
      stampCountry(quest.country.code);
    } catch { /* progression module optional */ }
    addStars(STAR_ON_FINISH, `Daily quest finished — ${quest.country.name}`);
  }

  return rec;
}

// Feb 2026 · Cycle 64.7 · Weekly completion lock.
// If the child finished today's World Quest, we don't want the same
// day's quest to re-appear on tomorrow's card. `weeklyLockActive`
// returns true when *any* day in the current ISO week already has a
// finishedAt marker, so the Today card can render a "Come back next
// Monday" state instead of another quest.
function _isoWeekMonday(d = new Date()) {
  const nd = new Date(d);
  const day = nd.getUTCDay(); // 0 = Sun
  const diff = day === 0 ? -6 : 1 - day;
  nd.setUTCDate(nd.getUTCDate() + diff);
  nd.setUTCHours(0, 0, 0, 0);
  return nd;
}

export function weeklyLockActive(nowIso = new Date().toISOString()) {
  const all = load();
  const weekStart = _isoWeekMonday(new Date(nowIso)).getTime();
  const weekEnd = weekStart + 7 * 86_400_000;
  for (const rec of Object.values(all)) {
    if (!rec || !rec.finishedAt) continue;
    const t = new Date(rec.finishedAt).getTime();
    if (t >= weekStart && t < weekEnd) return true;
  }
  return false;
}

/** Returns the most-recent finished quest record inside the current
 *  week, or null. Callers can use this to render a "You already
 *  aced this week — see you Monday!" state on the Today card. */
export function lastWeeklyCompletion(nowIso = new Date().toISOString()) {
  const all = load();
  const weekStart = _isoWeekMonday(new Date(nowIso)).getTime();
  const weekEnd = weekStart + 7 * 86_400_000;
  let latest = null;
  for (const [date, rec] of Object.entries(all)) {
    if (!rec || !rec.finishedAt) continue;
    const t = new Date(rec.finishedAt).getTime();
    if (t < weekStart || t >= weekEnd) continue;
    if (!latest || new Date(rec.finishedAt) > new Date(latest.finishedAt)) {
      latest = { ...rec, date };
    }
  }
  return latest;
}
